import { useState } from "react";
import { BtnPrimary, Card, Pill } from "../components/ui";
import { PEPTIDE_LIBRARY } from "../data/peptides";
import { S, css } from "../theme";

function Library({ protocol, setProtocol, setActive }) {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  const categories = ["All", ...new Set(PEPTIDE_LIBRARY.map(p => p.category))];
  const filtered = PEPTIDE_LIBRARY.filter(p =>
    (categoryFilter === "All" || p.category === categoryFilter) &&
    (p.name.toLowerCase().includes(search.toLowerCase()) || p.summary.toLowerCase().includes(search.toLowerCase()))
  );

  const inProtocol = (id) => protocol.find(p => p.id === id);

  return (
    <div style={{ padding: 40 }}>
      <h2 style={{ ...css.fontDisplay, fontSize: "1.8rem", fontWeight: 900, color: S.ink, letterSpacing: "-0.03em", marginBottom: 8 }}>Peptide Library</h2>
      <p style={{ color: S.muted, fontSize: "0.85rem", marginBottom: 28 }}>{PEPTIDE_LIBRARY.length} compounds with research breakdowns</p>

      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search compounds..." style={{ flex: 1, minWidth: 200, padding: "10px 16px", border: `1.5px solid ${S.border}`, borderRadius: 100, fontSize: "0.88rem", fontFamily: "'Inter', sans-serif", background: S.white, outline: "none" }} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {categories.map(c => (
            <button key={c} onClick={() => setCategoryFilter(c)} style={{ padding: "8px 16px", borderRadius: 100, border: `1.5px solid ${categoryFilter === c ? S.accent : S.border}`, background: categoryFilter === c ? S.accentLight : S.white, color: categoryFilter === c ? S.accent : S.muted, fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", fontFamily: "'Inter', sans-serif", transition: "all 0.2s" }}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: selected ? "1fr 380px" : "repeat(2,1fr)", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map(p => (
            <div key={p.id} onClick={() => setSelected(selected?.id === p.id ? null : p)} style={{ padding: "20px 24px", background: selected?.id === p.id ? S.accentLight : S.white, border: `1.5px solid ${selected?.id === p.id ? S.accent + "66" : S.border}`, borderRadius: 16, cursor: "pointer", transition: "all 0.2s" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", background: p.color, flexShrink: 0 }} />
                <span style={{ fontWeight: 700, fontSize: "0.95rem", color: S.accent2 }}>{p.name}</span>
                <Pill color={S.muted} bg={S.surface2}>{p.category}</Pill>
                {inProtocol(p.id) && <Pill color={S.accent} bg={S.accentLight}>In Protocol</Pill>}
              </div>
              <p style={{ fontSize: "0.8rem", color: S.muted, marginTop: 8, marginBottom: 0, lineHeight: 1.6 }}>{p.summary.slice(0, 100)}...</p>
              <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                {p.tags.map(t => <span key={t} style={{ fontSize: "0.65rem", padding: "2px 8px", background: S.surface2, color: S.muted, borderRadius: 100, fontWeight: 600 }}>{t}</span>)}
              </div>
            </div>
          ))}
        </div>

        {selected && (
          <div style={{ position: "sticky", top: 24, alignSelf: "flex-start" }}>
            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <div style={{ width: 14, height: 14, borderRadius: "50%", background: selected.color }} />
                    <span style={{ ...css.fontDisplay, fontWeight: 700, fontSize: "1.3rem", color: S.accent2 }}>{selected.name}</span>
                  </div>
                  <Pill color={S.muted} bg={S.surface2}>{selected.category}</Pill>
                </div>
                <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem", color: S.muted }}>✕</button>
              </div>

              <p style={{ fontSize: "0.85rem", color: S.ink2, lineHeight: 1.7, marginBottom: 20 }}>{selected.summary}</p>

              {[
                { label: "Common Dosing", value: selected.dosing },
                { label: "Route", value: selected.route },
                { label: "Typical Cycle", value: selected.cycle },
              ].map(r => (
                <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${S.border}`, fontSize: "0.82rem" }}>
                  <span style={{ color: S.muted }}>{r.label}</span>
                  <span style={{ fontWeight: 600, color: S.ink2 }}>{r.value}</span>
                </div>
              ))}

              <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
                {selected.tags.map(t => <span key={t} style={{ fontSize: "0.7rem", padding: "4px 10px", background: S.accentLight, color: S.accent, borderRadius: 100, fontWeight: 600 }}>{t}</span>)}
              </div>

              <div style={{ marginTop: 20 }}>
                {inProtocol(selected.id) ? (
                  <button onClick={() => setProtocol(prev => prev.filter(p => p.id !== selected.id))} style={{ width: "100%", padding: 14, borderRadius: 100, border: `1.5px solid #dc262633`, background: S.accent2Light, color: "#dc2626", fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer" }}>
                    Remove from Protocol
                  </button>
                ) : (
                  <BtnPrimary style={{ width: "100%" }} onClick={() => { setProtocol(prev => [...prev, { ...selected, dose: selected.dosing.split("–")[0].trim(), route: selected.route.split(" or ")[0], frequency: "Daily", notes: "", addedAt: new Date().toISOString(), startDate: new Date().toDateString() }]); setActive("protocol"); }}>
                    Add to My Protocol →
                  </BtnPrimary>
                )}
              </div>

              <p style={{ fontSize: "0.68rem", color: S.muted, marginTop: 14, lineHeight: 1.6, borderTop: `1px solid ${S.border}`, paddingTop: 14 }}>
                ⚠️ Educational reference only. Consult a physician before use.
              </p>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AI CHAT ─────────────────────────────────────────────────

export { Library };
